import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import InnerTabs, { TabT } from 'components/Tabs/InnerTabs'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import PublishBanner from 'scenes/Cms/components/PublishBanner'
import { useEscalationContactsController } from 'scenes/RecoveryAI/components/useEscalationContactsController'
import { history } from 'store'
import EditPermissionBanner from './EditPermissionBanner'
import FeedbackCollectionPane from './FeedbackCollectionPane'
import GuestExperiencePane from './GuestExperiencePane'
import { useFeedbackCollectionSubmitForm } from './hooks/useFeedbackCollectionSubmitForm'
import { useGuestExperienceSubmitForm } from './hooks/useGuestExperienceSubmitForm'
import { useReplyAddressSubmitForm } from './hooks/useReplyAddressSubmitForm'
import { useSettingsEditPermission } from './hooks/useSettingsEditPermission'
import NotificationsPane from './NotificationsPane'
import ReplyAddressPane from './ReplyAddressPane'
import {
  buildSettingsHash,
  DEFAULT_SETTINGS_TAB,
  isSettingsTab,
  readSettingsTabFromHash,
  SettingsTabs,
  SETTINGS_TAB_ORDER,
} from './tabs'

import { alert } from 'actions/flash'
import useDispatch from 'hooks/useDispatch'
import {
  selectIsFormDirty as selectFeedbackCollectionDirty,
  selectIsFormSubmitting as selectFeedbackCollectionSubmitting,
} from 'slices/orderFeedbackCollection/selectors'
import {
  selectIsFormDirty as selectGuestExperienceDirty,
  selectIsFormSubmitting as selectGuestExperienceSubmitting,
} from 'slices/orderFeedbackGuestExperience/selectors'
import {
  selectIsFormDirty as selectReplyAddressDirty,
  selectIsFormSubmitting as selectReplyAddressSubmitting,
  selectPublishDisabled as selectReplyAddressPublishDisabled,
} from 'slices/orderFeedbackReplyAddress/selectors'

const t = buildTranslate('order_ratings.settings')

const PUBLISH_PERMISSION_TOOLTIP_ID = 'settings-publish-permission'

type Props = {
  // The host page's own tab key (`Tabs.SETTINGS`), which owns the first
  // fragment segment. Passed in rather than re-declared here so the literal
  // 'settings' lives in exactly one place and the two files cannot drift.
  hashPrefix: string
}

// The pane behind the Order feedback page's Settings tab: a vertical sub-nav
// (the CMS Settings pattern — components/Tabs/InnerTabs) plus the panes behind
// it. Feedback collection, Reply address, Guest experience and Notifications
// all ship here.
//
// Each sub-tab shows ONLY its own panel, with ONE Publish bar at the bottom of
// the content column for all of them. Feedback collection is a switched pane
// like every other, NOT InnerTabs' `contentHeader`: v67 put its toggle and
// current-setup card above every sub-tab, and v69 corrected that as a bug
// ("each sub-tab shows only its own panel"). Its own Publish bar stays
// suppressed (see `showPublishBanner` below) in favour of the shared one.
//
// It is the one pane with `unmountInactive` false. The form-initialize hooks
// reset a slice to its last-PERSISTED value on every mount, so remounting this
// pane on re-entry would silently discard a staged toggle that the shared
// Publish bar still reports as dirty. Staying mounted (hidden) keeps the staged
// value and the Publish bar's view of it in agreement. The other panes are
// remounted on entry, as before.
//
// InnerTabs runs in CONTROLLED mode here. Uncontrolled, it writes bare keys
// into the shared URL hash, which is already owned by the page's outer tab
// system — an inner click would read as `#feedback_collection` and knock the
// page off its Settings tab. Controlled mode hands selection back to this,
// which writes the composite `#settings/<sub-tab>` instead.
const SettingsPane = ({ hashPrefix }: Props) => {
  const dispatch = useDispatch()

  // FR-60. Read here, not only in the panes, so the banner sits above the whole
  // sub-nav and explains every sub-tab's inert controls at once.
  const {
    editDisabled,
    permissionResolved,
    permissionLabel,
    permissionReason,
  } = useSettingsEditPermission()

  // Same shape as the host page's own hash handling: the viewer's choice lives
  // in the fragment, not in component state, so a sub-tab deep-links and
  // survives a refresh. Tracked with a listener because react-router-dom 4 has
  // no useLocation to observe the fragment with.
  const [activeTab, setActiveTab] = useState<SettingsTabs>(
    () => readSettingsTabFromHash(history.location.hash) ?? DEFAULT_SETTINGS_TAB
  )
  useEffect(
    () =>
      history.listen(location =>
        setActiveTab(
          readSettingsTabFromHash(location.hash) ?? DEFAULT_SETTINGS_TAB
        )
      ),
    []
  )

  // Write-only: the listener above turns the new fragment back into state, so
  // the hash stays the single source of truth for which sub-tab is active.
  // `replace`, not `push` — switching sub-tabs shouldn't stack history entries.
  // Spread the current location because history v4 only inherits `pathname`
  // from it and resets the query string and location.state otherwise.
  const handleChange = (key: string) => {
    if (!isSettingsTab(key)) return
    history.replace({
      ...history.location,
      hash: buildSettingsHash(hashPrefix, key),
    })
  }

  // One submit hook per form, called UNCONDITIONALLY regardless of which
  // sub-tab is active — never behind a branch on `activeTab`, so hook order
  // never depends on it. More than one form can be dirty at once: a merchant
  // may stage a change on one sub-tab and another before ever hitting Publish,
  // and the shared bar has to submit all of them from wherever it is clicked.
  const feedbackCollectionDirty = useSelector(selectFeedbackCollectionDirty)
  const feedbackCollectionSubmitting = useSelector(
    selectFeedbackCollectionSubmitting
  )
  const submitFeedbackCollection = useFeedbackCollectionSubmitForm()

  const replyAddressDirty = useSelector(selectReplyAddressDirty)
  const replyAddressSubmitting = useSelector(selectReplyAddressSubmitting)
  const replyAddressPublishDisabled = useSelector(
    selectReplyAddressPublishDisabled
  )
  const submitReplyAddress = useReplyAddressSubmitForm()

  const guestExperienceDirty = useSelector(selectGuestExperienceDirty)
  const guestExperienceSubmitting = useSelector(selectGuestExperienceSubmitting)
  const submitGuestExperience = useGuestExperienceSubmitForm()

  // Notifications has no separate submit-form hook file (unlike the other three,
  // its data layer is a shared RecoveryAI controller, not a slice) — called
  // here, ONCE, rather than inside NotificationsPane, and passed down as a
  // prop. Its own dirty/edit state lives in local useState, so a second call
  // from NotificationsPane would be a SEPARATE instance that could never see an
  // edit THIS instance (the one Publish below actually commits) knows about.
  const contacts = useEscalationContactsController({ notifyMode: 'staged' })

  const isAnyDirty =
    feedbackCollectionDirty ||
    replyAddressDirty ||
    guestExperienceDirty ||
    contacts.isDirty
  const isAnySubmitting =
    feedbackCollectionSubmitting ||
    replyAddressSubmitting ||
    guestExperienceSubmitting ||
    contacts.isSaving

  // A dirty form that cannot be saved has to stop the whole bar, not just its
  // own pane. `useReplyAddressSubmitForm` already refuses to PATCH an invalid
  // address, so without this the merchant gets an enabled Publish, a silent
  // no-op on that form, and — if another sub-tab is dirty and saves — a success
  // alert for an edit that was dropped.
  //
  // Guarded by `replyAddressDirty` because `selectPublishDisabled` answers for
  // that form ALONE (it is true whenever the form is clean). A clean reply
  // address must never veto a Publish that another sub-tab's dirty form has
  // earned.
  const replyAddressBlocksPublish =
    replyAddressDirty && replyAddressPublishDisabled

  const publishDisabled =
    !isAnyDirty || isAnySubmitting || editDisabled || replyAddressBlocksPublish

  // The slice-backed submit hooks (Feedback collection, Reply address, Guest
  // experience) already no-op on their own (clean form, in-flight save, or RBAC) — the `if` here is
  // only to skip an awaited no-op call for a form that has nothing to send.
  // More than one can be dirty at once, so each is submitted in turn rather
  // than branching on the active sub-tab. contacts.commit() has no
  // such built-in RBAC check of its own (it is a shared RecoveryAI controller
  // with no notion of this tab's `manage MerchantSettings` gate), so this
  // guards the handler as a whole, not just the button — mirrors the
  // guard Feedback collection's own submit hook already had. Notifications' own
  // commit() replaces the modal-era Save this pane used to have, and still
  // raises its own success/error alert — Feedback collection's hook dispatches
  // its own alert internally, this is the one call the container has to do
  // that for itself.
  const handlePublish = useCallback(async () => {
    if (editDisabled) return
    if (feedbackCollectionDirty) await submitFeedbackCollection()
    if (replyAddressDirty) await submitReplyAddress()
    if (guestExperienceDirty) await submitGuestExperience()
    if (contacts.isDirty) {
      const success = await contacts.commit()
      dispatch(
        alert({
          key: success ? 'success' : 'danger',
          message: success
            ? t('notifications.alert.submit_success')
            : t('notifications.alert.submit_error'),
          timeout: 5,
        })
      )
    }
  }, [
    editDisabled,
    feedbackCollectionDirty,
    submitFeedbackCollection,
    replyAddressDirty,
    submitReplyAddress,
    guestExperienceDirty,
    submitGuestExperience,
    contacts,
    dispatch,
  ])

  // A helper rather than a bare component in the map below, so the next new
  // sub-tab adds one branch here and one pane file, and nothing else moves.
  const paneFor = (tab: SettingsTabs): React.ReactNode => {
    switch (tab) {
      case SettingsTabs.FEEDBACK_COLLECTION:
        return <FeedbackCollectionPane showPublishBanner={false} />
      case SettingsTabs.REPLY_ADDRESS:
        return <ReplyAddressPane />
      case SettingsTabs.GUEST_EXPERIENCE:
        return <GuestExperiencePane />
      case SettingsTabs.NOTIFICATIONS:
        return <NotificationsPane contacts={contacts} />
      default:
        return null
    }
  }

  // `unmountInactive` resets a pane's own local state on re-entry. Feedback
  // collection opts out (see this component's header comment): remounting it
  // would re-initialize its slice from the persisted value and drop a staged
  // toggle the shared Publish bar still counts as dirty.
  const tabs: TabT[] = SETTINGS_TAB_ORDER.map(tab => ({
    key: tab,
    title: t(`${tab}.title`),
    component: paneFor(tab),
    unmountInactive: tab !== SettingsTabs.FEEDBACK_COLLECTION,
  }))

  return (
    <>
      {/* `permissionResolved` gates the BANNER only. `/order_feedback` is a
          plain <Route>, so a hard refresh renders this before
          `merchant_users/me` lands, and `editDisabled` reads true for the
          Login-only placeholder ability — identical to a real denial. The
          controls below stay disabled through that window (fail closed); only
          the banner's claim about permissions has to wait for an answer. */}
      <EditPermissionBanner
        editDisabled={editDisabled && permissionResolved}
        permissionLabel={permissionLabel}
      />
      <InnerTabs
        tabs={tabs}
        ariaLabel={t('tablist_label')}
        activeKey={activeTab}
        onChange={handleChange}
      />
      {/* The ONE Publish bar for the whole tab, below InnerTabs' content
          column rather than inside any one pane. Keyed to the PERMISSION
          alone, so an in-flight save is never explained as a permissions
          problem — same treatment FeedbackCollectionPane's own bar used to
          get. */}
      <PermissionTooltip
        disabled={Boolean(permissionReason)}
        reason={permissionReason}
        id={PUBLISH_PERMISSION_TOOLTIP_ID}
        placement="left"
      >
        {/* No `message` prop: that one replaces the whole sentence, and the
            design's bar is PublishBanner's own default — a bold
            "Ready to go live?" followed by the "Hit the “Publish” button …"
            instruction, already spelled with typographic quotes in
            `cms.components.publish_banner`. */}
        <PublishBanner
          buttonType="button"
          buttonText={t('publish')}
          isLoading={isAnySubmitting}
          disabled={publishDisabled}
          onClick={handlePublish}
        />
      </PermissionTooltip>
    </>
  )
}

export default SettingsPane
